import type { ChainExecution } from "./types";

/** Typed outputs of the demo's chains, plus tolerant extractors (LLM output is never perfectly regular). */

const isObj = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === "object" && !Array.isArray(v);

/** Node outputs in node order (node-2 before node-10), JSON strings parsed. */
export function nodeOutputs(execution: ChainExecution): { nodeId: string; output: unknown }[] {
  const nr = execution.node_results ?? {};
  const num = (id: string) => Number(id.match(/(\d+)$/)?.[1] ?? Number.MAX_SAFE_INTEGER);
  return Object.keys(nr)
    .sort((a, b) => num(a) - num(b))
    .map((nodeId) => {
      const node = nr[nodeId];
      let o: unknown = node && node.output !== undefined ? node.output : node;
      if (typeof o === "string") {
        try {
          o = JSON.parse(o);
        } catch {
          /* text */
        }
      }
      if (isObj(o) && isObj(o.output) && Object.keys(o).length <= 3) o = o.output;
      return { nodeId, output: o };
    });
}

// ---------- meal plans ----------

export interface MacroTotals {
  calories?: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  fiber_g?: number;
}

/** A Passio food matched to a planned ingredient (from linkIngredients). */
export interface MealPlanLinkedFood {
  displayName?: string;
  shortName?: string;
  longName?: string;
  refCode?: string;
  iconId?: string;
  brandName?: string;
  [key: string]: unknown;
}

export interface MealPlanIngredient {
  ingredient: { ingredientName?: string; weightGrams?: number; portionQuantity?: number; portionSize?: string; [key: string]: unknown };
  passio_food?: MealPlanLinkedFood | null;
  grams?: number | null;
  macros?: MacroTotals & { grams?: number };
}

export interface MealPlanMeal {
  mealName?: string;
  recipeTitle?: string;
  title?: string;
  instructions?: string | string[];
  ingredients_detailed?: MealPlanIngredient[];
  macro_totals?: MacroTotals;
  [key: string]: unknown;
}

export interface MealPlanDay {
  day_number?: number;
  macro_target?: MacroTotals;
  macro_totals?: MacroTotals;
  meals: MealPlanMeal[];
  [key: string]: unknown;
}

export interface MealPlan {
  constraints: Record<string, unknown>;
  macro_targets: MacroTotals | null;
  days: MealPlanDay[];
}

export interface MealPlanPdf {
  pdfUrl: string | null;
  filename: string | null;
  skipped: boolean;
}

/** A household member as parsed by the Family chain's first step. */
export interface MealPlanMember {
  name?: string;
  role?: string;
  age?: number | null;
  allergies?: string[];
  dislikes?: string[];
  preferences?: string[];
  health?: string[];
  notes?: string | null;
}

export interface MealPlanHousehold {
  size?: number;
  members: MealPlanMember[];
  constraints: Record<string, unknown>;
}

const NAME_KEYS = ["ingredientName", "name", "ingredient", "food", "foodName", "item", "title", "description"];

/** An ingredient's display name, whichever key the model used. */
export function ingredientLabel(it: MealPlanIngredient): string {
  const raw = it.ingredient as Record<string, unknown> | undefined;
  for (const k of NAME_KEYS) {
    const v = raw?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return it.passio_food?.displayName ?? "";
}

/** Grams for an ingredient, from the linked result or the model's own weight. */
export function ingredientGrams(it: MealPlanIngredient): number | undefined {
  const raw = (it.ingredient ?? {}) as Record<string, unknown>;
  for (const v of [it.grams, raw.weightGrams, raw.grams, raw.weight_g, raw.quantity_g]) if (typeof v === "number" && Number.isFinite(v) && v > 0) return v;
  return undefined;
}

/** True when the ingredient was matched to a Passio food. */
export const isLinked = (it: MealPlanIngredient) => Boolean(it.passio_food?.displayName || it.passio_food?.refCode);

const MACRO_KEYS = ["calories", "protein_g", "carbs_g", "fat_g", "fiber_g"] as const;
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

/**
 * Macros that can describe real food: at most ~9.5 kcal and ~1 g of protein + carbs + fat per gram.
 * Anything denser is a unit error upstream, and is dropped rather than inflating the totals.
 */
function plausibleMacros(macros: unknown, grams: number | undefined): MacroTotals | undefined {
  if (!isObj(macros)) return undefined;
  const out: MacroTotals = {};
  for (const k of MACRO_KEYS) {
    const v = num(macros[k]);
    if (v !== undefined && v >= 0) out[k] = v;
  }
  if (out.calories === undefined && out.protein_g === undefined && out.carbs_g === undefined && out.fat_g === undefined) return undefined;
  if (grams && grams > 0) {
    if ((out.calories ?? 0) / grams > 9.5) return undefined;
    if (((out.protein_g ?? 0) + (out.carbs_g ?? 0) + (out.fat_g ?? 0)) / grams > 1.05) return undefined;
  }
  return out;
}

function sumTotals(list: (MacroTotals | undefined)[]): MacroTotals | undefined {
  const present = list.filter((t): t is MacroTotals => Boolean(t));
  if (!present.length) return undefined;
  const out: MacroTotals = {};
  for (const k of MACRO_KEYS) {
    const vals = present.map((t) => t[k]).filter((v): v is number => v !== undefined);
    if (vals.length) out[k] = vals.reduce((a, b) => a + b, 0);
  }
  return out;
}

/** A target as flat numbers; Family chains sometimes nest it (per_adult_daily, per_person_daily, daily). */
export function macroTarget(raw: unknown): MacroTotals | null {
  if (!isObj(raw)) return null;
  if (num(raw.calories) !== undefined || num(raw.protein_g) !== undefined) return plausibleMacros(raw, undefined) ?? null;
  for (const k of ["per_adult_daily", "per_person_daily", "daily", "per_day", "adult"]) if (isObj(raw[k])) return macroTarget(raw[k]);
  return null;
}

/**
 * Meals always get `ingredients_detailed` (unlinked raw `ingredients`, strings or objects, are wrapped),
 * and their totals are recomputed from those ingredients so meal and day totals always add up.
 */
function normalizeMeal(m: Record<string, unknown>): MealPlanMeal {
  const detailed = Array.isArray(m.ingredients_detailed) ? m.ingredients_detailed.filter(isObj) : null;
  const raw = Array.isArray(m.ingredients) ? m.ingredients : [];
  const items = detailed?.length ? detailed : raw.map((x) => (isObj(x) ? { ingredient: x } : { ingredient: { ingredientName: String(x) } }));
  const ingredients = items.map((it) => {
    const base = { ...it, ingredient: isObj(it.ingredient) ? it.ingredient : typeof it.ingredient === "string" ? { ingredientName: it.ingredient } : {} } as MealPlanIngredient;
    const passio_food = isObj(base.passio_food) && (base.passio_food.displayName || base.passio_food.refCode) ? base.passio_food : null;
    return { ...base, passio_food, macros: plausibleMacros(base.macros, ingredientGrams(base)) };
  });
  return { ...m, ingredients_detailed: ingredients, macro_totals: sumTotals(ingredients.map((i) => i.macros)) } as MealPlanMeal;
}

function normalizeDay(d: Record<string, unknown>): MealPlanDay {
  const meals = Array.isArray(d.meals) ? d.meals.filter(isObj).map(normalizeMeal) : [];
  return { ...d, meals, macro_target: macroTarget(d.macro_target) ?? undefined, macro_totals: sumTotals(meals.map((m) => m.macro_totals)) } as MealPlanDay;
}

/** Find the assembled plan (latest node with `days[].meals`), the PDF result and, for Family, the household. */
export function mealPlanFromExecution(execution: ChainExecution): { plan: MealPlan | null; pdf: MealPlanPdf | null; household: MealPlanHousehold | null } {
  let plan: MealPlan | null = null;
  let pdf: MealPlanPdf | null = null;
  let household: MealPlanHousehold | null = null;
  for (const { output } of nodeOutputs(execution)) {
    if (!isObj(output)) continue;
    if (!household && isObj(output.household) && Array.isArray(output.household.members)) {
      household = {
        size: typeof output.household.size === "number" ? output.household.size : undefined,
        members: output.household.members.filter(isObj) as MealPlanMember[],
        constraints: isObj(output.household_constraints) ? output.household_constraints : {},
      };
    }
    if ("pdf_url" in output || output.skipped === true) {
      pdf = { pdfUrl: typeof output.pdf_url === "string" ? output.pdf_url : null, filename: typeof output.filename === "string" ? output.filename : null, skipped: output.skipped === true };
      continue;
    }
    const days = output.days;
    if (Array.isArray(days) && days.some((d) => isObj(d) && Array.isArray(d.meals))) {
      const prev = plan as MealPlan | null;
      plan = {
        constraints: isObj(output.constraints) ? output.constraints : (prev?.constraints ?? {}),
        macro_targets: macroTarget(output.macro_targets) ?? prev?.macro_targets ?? null,
        days: days.filter(isObj).map(normalizeDay),
      };
    }
  }
  return { plan, pdf, household };
}

/** Instruction text as numbered steps (handles "1. ... 2. ...", one step per line, or plain sentences). */
export function instructionSteps(instr: unknown): string[] {
  if (Array.isArray(instr)) return instr.map(String).map((s) => s.trim()).filter(Boolean);
  if (typeof instr !== "string" || !instr.trim()) return [];
  const text = instr.trim();
  const strip = (s: string) => s.replace(/^\s*(\d{1,2}[.)]|[-•*])\s*/, "").trim();
  // Only "1." "2." "3." in order count as step markers, so "multiply by 3." isn't one.
  const cuts: number[] = [];
  for (const m of text.matchAll(/(?:^|\s)(\d{1,2})[.)](?=\s)/g)) {
    if (Number(m[1]) === cuts.length + 1) cuts.push(m.index + m[0].length - m[0].trimStart().length);
  }
  if (cuts.length > 1) return [0, ...cuts].map((at, i, all) => strip(text.slice(at, all[i + 1]))).filter(Boolean);
  const lines = text.split(/\n+/).map(strip).filter(Boolean);
  if (lines.length > 1) return lines;
  return [strip(text)];
}

// ---------- GI coach (glp1_assistant) ----------

export type GiLevel = "low" | "moderate" | "high" | "unknown";

export interface GiTrigger {
  code?: string;
  label?: string;
  basis?: string;
  explanation?: string;
}

export interface GiMeal {
  meal_id?: string;
  level?: GiLevel;
  food_identified?: string[];
  triggers?: (GiTrigger | string)[];
  uncertainties?: string[];
  one_line_explanation?: string;
}

export interface GiRecommendation {
  id?: string;
  title?: string;
  detail?: string;
  applies_to_meal_ids?: string[];
}

export interface GiSource {
  url?: string | null;
  title?: string;
  pdf_pages?: (number | string)[];
  source_id?: string;
}

export interface GiCoachResponse {
  schema_version?: string;
  /** e.g. food_coaching, general_question, insufficient_information, scope_boundary, safety_escalation */
  response_kind?: string;
  region?: string;
  output_language?: string;
  short_answer?: string;
  long_answer?: string;
  key_points?: string[];
  food_assessment?: { scope?: string; overall_level?: GiLevel; overall_explanation?: string; meals?: GiMeal[] };
  recommendations?: (GiRecommendation | string)[];
  follow_up_questions?: string[];
  safety?: { urgency?: "none_identified" | "routine_review" | "urgent" | "emergency" | string; message?: string | null; red_flags?: unknown[]; suppress_food_coaching?: boolean };
  limitations?: string[];
  disclaimer?: string;
  sources?: (GiSource | string)[];
  metadata?: Record<string, unknown>;
}

export function giCoachFromOutput(output: unknown): GiCoachResponse | null {
  if (!isObj(output)) return null;
  if ("short_answer" in output || "response_kind" in output || "safety" in output) return output as GiCoachResponse;
  return isObj(output.output) ? giCoachFromOutput(output.output) : null;
}

// ---------- grocery replacements (keto / low-UPF replacement chain) ----------

export interface GroceryNutrients {
  calories_kcal?: number;
  protein_g?: number;
  fat_g?: number;
  carbs_g?: number;
  fiber_g?: number;
  total_sugars_g?: number;
  saturated_fat_g?: number;
  sodium_mg?: number;
  [key: string]: number | undefined;
}

export interface GroceryProduct {
  id?: string;
  name?: string;
  brand?: string;
  serving?: string;
  serving_grams?: number;
  ingredients?: string;
  nutrients?: GroceryNutrients;
  net_carbs_g?: number;
  /** 0..1, higher = more keto friendly */
  keto_score?: number;
  /** 0..1, higher = less processed */
  upf_score?: number;
  jfm_score?: number;
  /** 0..1 rank from the AI step (replacements only) */
  ai_score?: number;
  ai_reason?: string;
  notes?: string[];
  tags?: string[];
  country?: string;
  ref_code?: string;
  product_code?: string;
  source?: string;
  preferred?: boolean;
  category_match?: boolean;
}

export interface GroceryItem {
  original_item: string;
  search_terms?: string[];
  original_item_details?: GroceryProduct | null;
  suggested_replacements: GroceryProduct[];
  original_already_good?: boolean;
  had_internal_matches?: boolean;
  used_web_fallback?: boolean;
  stats?: Record<string, unknown>;
}

export interface GroceryReplacementResult {
  items: GroceryItem[];
  summary?: { total_items?: number; items_with_replacements?: number; items_already_good?: number; total_found?: number; [key: string]: unknown };
}

/** The assemble node's `{items:[{original_item, suggested_replacements}], summary}`; earlier `{items}` nodes are skipped. */
export function groceryReplacementsFromExecution(execution: ChainExecution): GroceryReplacementResult | null {
  let best: GroceryReplacementResult | null = null;
  for (const { output } of nodeOutputs(execution)) {
    if (!isObj(output) || !Array.isArray(output.items)) continue;
    const items = output.items.filter(isObj);
    if (!items.some((it) => "suggested_replacements" in it || "original_item_details" in it)) continue;
    best = {
      items: items.map((it) => ({ ...it, original_item: String(it.original_item ?? ""), suggested_replacements: Array.isArray(it.suggested_replacements) ? it.suggested_replacements.filter(isObj) : [] }) as GroceryItem),
      summary: isObj(output.summary) ? output.summary : undefined,
    };
  }
  return best;
}
